import {
  SacChamado,
  SacChamadoDetalhe,
  SacCotacao,
  SacMensagem,
  SacMotivoGrupo,
} from "@/types/Chamados";
import api from "./api";
import apiHmlg from "./apiHmlg";

const API_TARGET = import.meta.env.VITE_API_TARGET || "prod";

const ApiAmbient = API_TARGET.toLowerCase() === "hmlg" ? apiHmlg : api;

export const sacMotivos = [];

export const fetchSacMotivos = async (): Promise<SacMotivoGrupo[]> => {
  const response = await ApiAmbient.get("/cliente/api_sac_motivos.php");

  let data = response.data;

  if (typeof data === "string") {
    try {
      if (!data.startsWith("[")) {
        data = "[" + data.replace(/}\s*{/g, "},{") + "]";
      }
      data = JSON.parse(data);
    } catch (error) {
      console.error("Erro ao fazer parse dos dados:", error);
      throw error;
    }
  }

  return data;
};

export const fetchSacCotacoes = async (
  cli_codigo: string
): Promise<SacCotacao[]> => {
  const response = await ApiAmbient.post("/cliente/api_sac_cotacoes.php", {
    cli_codigo,
  });

  let data: unknown = response.data;

  if (typeof data === "string") {
    let str = data;
    try {
      if (!str.trim().startsWith("[")) {
        str = "[" + str.replace(/}\s*{/g, "},{") + "]";
      }
      data = JSON.parse(str);
    } catch (error) {
      console.error("Erro ao fazer parse das cotações:", error);
      throw error;
    }
  }

  return data as SacCotacao[];
};

export const gravarChamado = async (
  sac_data_evento: string,
  sac_descricao: string,
  cli_codigo: string,
  cve_codigo: string,
  stc_codigo: string,
  files?: File[]
): Promise<{ sucesso: boolean; mensagem: string; sac_codigo: string }> => {
  const formData = new FormData();
  formData.append("sac_data_evento", sac_data_evento);
  formData.append("sac_descricao", sac_descricao);
  formData.append("cli_codigo", cli_codigo);
  formData.append("cve_codigo", cve_codigo);
  formData.append("stc_codigo", stc_codigo);

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files[]", file);
    });
  }

  const response = await ApiAmbient.post(
    "/cliente/api_sac_gravar_chamado.php",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export const fetchSacChamados = async (
  cli_codigo: string
): Promise<SacChamado[]> => {
  const response = await ApiAmbient.post(
    "/cliente/api_sac_chamados_cliente.php",
    {
      cli_codigo,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  let data: unknown = response.data;

  if (typeof data === "string") {
    let str = data;
    try {
      if (!str.trim().startsWith("[")) {
        str = "[" + str.replace(/}\s*{/g, "},{") + "]";
      }
      data = JSON.parse(str);
    } catch (error) {
      console.error("Erro ao fazer parse dos chamados:", error);
      throw error;
    }
  }

  return data as SacChamado[];
};

export const responderChamado = async (
  sac_codigo: string,
  mensagem: string,
  cli_codigo: string,
  files?: File[]
): Promise<{ sucesso: boolean; mensagem: string }> => {
  const formData = new FormData();
  formData.append("sac_codigo", sac_codigo);
  formData.append("mensagem", mensagem);
  formData.append("cli_codigo", cli_codigo);

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files[]", file); // Adiciona [] para indicar array
    });
  }

  const response = await ApiAmbient.post(
    "/cliente/api_sac_responder_chamado.php",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export const encerrarChamado = async (
  sac_codigo: string,
  sac_cliente_encerrou: string = "1"
): Promise<{ sucesso: boolean; mensagem: string; sac_codigo: number }> => {
  const response = await ApiAmbient.post(
    "/cliente/api_sac_encerrar_chamado.php",
    {
      sac_codigo,
      sac_cliente_encerrou,
    },
    {
      params: {
        sac_codigo,
        sac_cliente_encerrou,
      },
    }
  );

  return response.data;
};

export const registrarNPS = async (
  sac_codigo: string,
  sac_nps_nota: number,
  sac_nps_obs: string = ""
): Promise<{ sucesso: boolean; mensagem: string }> => {
  const response = await ApiAmbient.post("/cliente/api_sac_registrar_nps.php", {
    sac_codigo,
    sac_nps_nota: sac_nps_nota.toString(),
    sac_nps_obs,
  });

  return response.data;
};

export const fetchChamadoDetalhe = async (
  sac_codigo: string
): Promise<SacChamadoDetalhe> => {
  const response = await ApiAmbient.post(
    "/cliente/api_sac_detalhe_chamado.php",
    {
      sac_codigo,
    }
  );

  let data: unknown = response.data;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (error) {
      console.error("Erro ao fazer parse do detalhe do chamado:", error);
      throw error;
    }
  }

  return data as SacChamadoDetalhe;
};

export const gravarMensagemChamado = async (
  sac_codigo: string,
  smg_texto: string,
  smg_cliente_enviou: string = "1",
  files?: File[]
): Promise<{ sucesso: boolean; mensagem: string; smg_codigo: string }> => {
  const formData = new FormData();
  formData.append("sac_codigo", sac_codigo);
  formData.append("smg_texto", smg_texto);
  formData.append("smg_cliente_enviou", smg_cliente_enviou);

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files[]", file);
    });
  }

  const response = await ApiAmbient.post(
    "/cliente/api_sac_gravar_mensagens.php",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export const fetchMensagensChamado = async (
  sac_codigo: string
): Promise<SacMensagem[]> => {
  const response = await ApiAmbient.get(
    "/cliente/api_sac_mensagens_chamado.php",
    {
      params: {
        sac_codigo,
      },
    }
  );

  let data: unknown = response.data;

  if (typeof data === "string") {
    let str = data;
    try {
      if (!str.trim().startsWith("[")) {
        str = "[" + str.replace(/}\s*{/g, "},{") + "]";
      }
      data = JSON.parse(str);
    } catch (error) {
      console.error("Erro ao fazer parse das mensagens:", error);
      throw error;
    }
  }

  return data as SacMensagem[];
};
